// components/landing/DataServicesSection.tsx
'use client';

import React from 'react';
import { Cloud, Database, Webhook, Share2, Lock, Zap } from 'lucide-react';

const services = [
  {
    name: '기상청 날씨 API',
    category: '공공 데이터',
    status: 'connected',
    icon: Cloud,
    gradient: 'from-blue-500 to-cyan-500',
  },
  {
    name: '농촌진흥청 농업정보',
    category: '공공 데이터',
    status: 'connected',
    icon: Database,
    gradient: 'from-green-500 to-emerald-500',
  },
  {
    name: 'AWS IoT Core',
    category: '클라우드',
    status: 'connected',
    icon: Cloud,
    gradient: 'from-orange-500 to-red-500',
  },
  {
    name: '병해충 진단 AI',
    category: 'AI 서비스',
    status: 'connected',
    icon: Zap,
    gradient: 'from-purple-500 to-pink-500',
  },
];

export default function DataServicesSection() {
  return (
    <section className="relative bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-gray-900 dark:to-emerald-900/20 overflow-hidden py-24 lg:py-32">
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* 왼쪽: 설명 */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center px-4 py-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-full text-emerald-700 dark:text-emerald-300 text-sm font-medium mb-6">
              <Share2 className="w-4 h-4 mr-2" />
              연계 서비스
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              다양한 서비스와
              <span className="block text-emerald-600 dark:text-emerald-400 mt-2">완벽한 통합</span>
            </h2>

            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              공공 데이터, 클라우드 서비스, AI 분석까지. 농업에 필요한 모든 외부 서비스를 안전하게 연동하여 통합 관리합니다.
            </p>

            {/* 주요 기능 */}
            <div className="space-y-4 mb-8">
              {[
                { icon: Lock, text: 'OAuth 2.0 보안 인증', desc: '안전한 API 연동' },
                { icon: Webhook, text: '실시간 웹훅', desc: '이벤트 기반 알림' },
                { icon: Database, text: '데이터 캐싱', desc: '빠른 응답 속도' },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                      <Icon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">{item.text}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{item.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 기술 스택 */}
            <div className="flex flex-wrap gap-2">
              {['REST API', 'OAuth 2.0', 'Redis', 'Nginx', 'Docker'].map((tech) => (
                <span
                  key={tech}
                  className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full text-sm font-medium"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* 오른쪽: 서비스 카드 */}
          <div className="relative">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">연결된 서비스</h3>

              <div className="grid grid-cols-2 gap-4">
                {services.map((service, index) => {
                  const Icon = service.icon;
                  return (
                    <div
                      key={index}
                      className="relative overflow-hidden rounded-xl p-4  shadow-lg group hover:scale-105 transition-transform"
                      style={{
                        backgroundImage: `linear-gradient(135deg, ${service.gradient})`,
                      }}
                    >
                      <div className="relative z-10">
                        <Icon className="w-8 h-8 mb-3 opacity-90" />
                        <div className="text-sm font-semibold mb-1">{service.name}</div>
                        <div className="text-xs opacity-80">{service.category}</div>
                        <div className="mt-3 flex items-center gap-1">
                          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                          <span className="text-xs">연결됨</span>
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>
                  );
                })}
              </div>

              {/* API 호출 통계 */}
              <div className="mt-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">오늘의 API 호출</div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">12.5K</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">총 호출</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">99.8%</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">성공률</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">45ms</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">평균 응답</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
